import React, { useEffect, useMemo, useRef, useState } from "react";
import "./Modal.css";
import { formatLongDate, minutesToDate } from "../dateUtils";
import { buildTimeOptions, formatTimeFromMinutes, minutesFromDate } from "../timeUtils";
import SubjectSelect from "./SubjectSelect";
import {
  fetchAvailabilityBlocks,
  fetchScheduledSessions,
  fetchDayConfigsForDates,
} from "../schedulingApi";
import {
  dayKeyFromDate as dayKeyFromScheduleDate,
  resolveScheduleForDate,
} from "../scheduleSettingsUtils";
import { FiX } from "react-icons/fi";
import ModalCalendarPanel from "../student_calendar/ModalCalendarPanel";

// ─── Pure helpers ─────────────────────────────────────────────────────────────

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function overlaps(aStart, aEnd, bStart, bEnd) {
  return aStart < bEnd && bStart < aEnd;
}

function isCanceledStatus(status) {
  return String(status || "").toLowerCase().includes("cancel");
}

function resolveRange(ctx) {
  const start = Number.isFinite(ctx?.availabilityStartMin)
    ? ctx.availabilityStartMin
    : Number.isFinite(ctx?.block?.startMin) ? ctx.block.startMin : 0;
  const end = Number.isFinite(ctx?.availabilityEndMin)
    ? ctx.availabilityEndMin
    : Number.isFinite(ctx?.maxEndMin) ? ctx.maxEndMin
    : Number.isFinite(ctx?.block?.endMin) ? ctx.block.endMin : 24 * 60;
  return { startMin: start, endMin: Math.max(start + 5, end) };
}

function dateKeyLocal(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function dayKeyFromDate(dateObj) {
  return dayKeyFromScheduleDate(dateObj);
}

function isFullyContainedInAvailability(blocks, start, end) {
  for (const block of blocks || []) {
    if (block.unavailable) continue;
    if (new Date(block.start) <= start && new Date(block.end) >= end) return true;
  }
  return false;
}

function maxConcurrentWithinRange(existingSessions, startMin, endMin, tutorUid = null) {
  const events = [];
  for (const session of existingSessions || []) {
    if (tutorUid && session.tutorUid !== tutorUid) continue;
    if (isCanceledStatus(session.status)) continue;
    const sessionStart = minutesFromDate(new Date(session.start));
    const sessionEnd = minutesFromDate(new Date(session.end));
    if (!overlaps(sessionStart, sessionEnd, startMin, endMin)) continue;
    events.push({ minute: Math.max(sessionStart, startMin), delta: 1 });
    events.push({ minute: Math.min(sessionEnd, endMin), delta: -1 });
  }
  events.sort((a, b) => a.minute !== b.minute ? a.minute - b.minute : a.delta - b.delta);
  let active = 0, peak = 0;
  for (const ev of events) { active += ev.delta; if (active > peak) peak = active; }
  return peak;
}

function canFitTutor(existingSessions, tutorUid, startMin, endMin) {
  return maxConcurrentWithinRange(existingSessions, startMin, endMin, tutorUid) < 3;
}

function seatsLeftForTutor(existingSessions, tutorUid, startMin, endMin) {
  return Math.max(0, 3 - maxConcurrentWithinRange(existingSessions, startMin, endMin, tutorUid));
}

function resolveLane(existingSessions, startMin, endMin, tutorUid) {
  const entries = (existingSessions || [])
    .filter((s) => {
      if (s.tutorUid !== tutorUid) return false;
      if (isCanceledStatus(s.status)) return false;
      const ss = minutesFromDate(new Date(s.start));
      const se = Math.max(ss + 5, minutesFromDate(new Date(s.end)));
      return overlaps(ss, se, startMin, endMin);
    })
    .map((s) => {
      const ss = minutesFromDate(new Date(s.start));
      const se = Math.max(ss + 5, minutesFromDate(new Date(s.end)));
      return { id: s.id, startMin: ss, endMin: se };
    });
  entries.push({ id: "__candidate__", startMin, endMin });
  entries.sort((a, b) => {
    if (a.startMin !== b.startMin) return a.startMin - b.startMin;
    if (a.endMin !== b.endMin) return a.endMin - b.endMin;
    if (a.id === "__candidate__") return 1;
    if (b.id === "__candidate__") return -1;
    return 0;
  });
  const laneEnds = [-Infinity, -Infinity, -Infinity];
  const laneById = new Map();
  for (const entry of entries) {
    let lane = null;
    for (let idx = 0; idx < 3; idx += 1) {
      if (laneEnds[idx] <= entry.startMin) { lane = idx; break; }
    }
    if (lane == null) {
      if (entry.id === "__candidate__") return null;
      lane = laneEnds.indexOf(Math.min(...laneEnds));
    }
    laneEnds[lane] = Math.max(laneEnds[lane], entry.endMin);
    laneById.set(entry.id, lane);
  }
  return laneById.get("__candidate__") ?? null;
}

function pickBlock(timeBlocks, startMin, endMin, fallbackBlock) {
  if (!Array.isArray(timeBlocks) || !timeBlocks.length) return fallbackBlock || null;
  const exact = timeBlocks.find((b) => startMin >= b.startMin && startMin < b.endMin);
  if (exact) return exact;
  let best = timeBlocks[0], bestOverlap = -1;
  for (const b of timeBlocks) {
    const ov = Math.max(0, Math.min(endMin, b.endMin) - Math.max(startMin, b.startMin));
    if (ov > bestOverlap) { bestOverlap = ov; best = b; }
  }
  return best || fallbackBlock || null;
}

function ymdToDate(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d);
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function CreateSessionModal({
  isOpen,
  onClose,
  ctx,
  students,
  tutors = [],
  onCreate,
  isMasterMode = false,
  // loadStudentSessionsForRange is no longer used — ModalCalendarPanel subscribes directly
}) {
  const [studentSearch, setStudentSearch] = useState("");
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  const [subject, setSubject] = useState("");
  const [note, setNote] = useState("");
  const [tutorUid, setTutorUid] = useState("");
  const [dateStr, setDateStr] = useState("");
  const [startMin, setStartMin] = useState(0);
  const [endMin, setEndMin] = useState(0);

  // Day-level scheduling data (for tutor availability check in the form)
  const [dayAvailability, setDayAvailability] = useState([]);
  const [daySessions, setDaySessions] = useState([]);
  const [dayTimeBlocks, setDayTimeBlocks] = useState([]);
  const [isDayClosed, setIsDayClosed] = useState(false);
  const [dayRange, setDayRange] = useState({ startMin: 8 * 60, endMin: 22 * 60 });

  const dropdownRef = useRef(null);
  const range = useMemo(() => resolveRange(ctx), [ctx]);
  const dateObj = useMemo(() => (dateStr ? ymdToDate(dateStr) : null), [dateStr]);

  const selectedStudent = useMemo(() => students.find((s) => s.id === selectedStudentId), [students, selectedStudentId]);
  const selectedTutor   = useMemo(() => tutors.find((t) => t.id === tutorUid), [tutors, tutorUid]);

  const filteredStudents = useMemo(() => {
    if (!studentSearch) return students;
    const lower = studentSearch.toLowerCase();
    return students.filter((s) => s.name.toLowerCase().includes(lower));
  }, [students, studentSearch]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reset on open
  useEffect(() => {
    if (!isOpen || !ctx) return;
    setStudentSearch("");
    setSelectedStudentId("");
    setShowDropdown(false);
    setSubject("");
    setNote("");
    setTutorUid(ctx.tutorUid || "");

    const baseDate = ctx.date ? new Date(ctx.date) : new Date();
    setDateStr(dateKeyLocal(baseDate));

    const clickedStart = Number.isFinite(ctx.startMin) ? ctx.startMin
      : Number.isFinite(ctx.subStartMin) ? ctx.subStartMin
      : Number.isFinite(ctx.block?.startMin) ? ctx.block.startMin
      : range.startMin;

    const safeStart = clamp(clickedStart, range.startMin, range.endMin - 5);
    const defaultEndTarget = Number.isFinite(ctx.defaultEndMin) ? ctx.defaultEndMin : safeStart + 110;
    const safeEnd = clamp(Math.max(safeStart + 5, defaultEndTarget), safeStart + 5, range.endMin);
    setStartMin(safeStart);
    setEndMin(safeEnd);
  }, [isOpen, ctx, range.startMin, range.endMin]);

  // Fetch day-level availability data (for form validation)
  useEffect(() => {
    let alive = true;
    const fetchDayData = async () => {
      if (!isOpen || !ctx || !dateStr) return;
      const targetDate = ymdToDate(dateStr);
      const startOfDay = new Date(targetDate); startOfDay.setHours(0, 0, 0, 0);
      const endOfDay   = new Date(targetDate); endOfDay.setHours(23, 59, 59, 999);

      if (isMasterMode) {
        const fallbackBlock = ctx.block ? [ctx.block] : [{
          id: `ctx_${ctx.dayKey || "day"}`, startMin: range.startMin, endMin: range.endMin,
        }];
        if (!alive) return;
        setDayAvailability([{ uid: ctx.tutorUid, start: minutesToDate(targetDate, range.startMin), end: minutesToDate(targetDate, range.endMin) }]);
        setDaySessions((ctx.existingSessions || []).map((s) => ({ ...s, tutorUid: ctx.tutorUid })));
        setIsDayClosed(false);
        setDayRange({ startMin: range.startMin, endMin: range.endMin });
        setDayTimeBlocks(Array.isArray(ctx.timeBlocks) && ctx.timeBlocks.length ? ctx.timeBlocks : fallbackBlock);
        return;
      }

      try {
        const [avail, sessions, cfgs] = await Promise.all([
          fetchAvailabilityBlocks({ rangeStart: startOfDay, rangeEnd: endOfDay }),
          fetchScheduledSessions({ rangeStart: startOfDay, rangeEnd: endOfDay }),
          fetchDayConfigsForDates([startOfDay]),
        ]);
        if (!alive) return;
        setDayAvailability(avail || []);
        setDaySessions(sessions || []);
        const config = cfgs?.[dateStr];
        const resolved = resolveScheduleForDate(startOfDay, config);
        setIsDayClosed(!!resolved.isClosed);
        setDayRange({ startMin: resolved.startMin, endMin: resolved.endMin });
        setDayTimeBlocks(
          Array.isArray(config?.timeBlocks) && config.timeBlocks.length
            ? config.timeBlocks
            : Array.isArray(ctx.timeBlocks) ? ctx.timeBlocks : []
        );
      } catch {
        if (!alive) return;
        setDayAvailability([]);
        setDaySessions([]);
        setDayTimeBlocks([]);
        setIsDayClosed(false);
        setDayRange({ startMin: range.startMin, endMin: range.endMin });
      }
    };
    fetchDayData();
    return () => { alive = false; };
  }, [isOpen, ctx, dateStr, isMasterMode, range.startMin, range.endMin]);

  const availableTutors = useMemo(() => {
    if (!dateObj || isDayClosed) return [];
    if (isMasterMode) {
      const fb = tutors.find((t) => t.id === ctx?.tutorUid) || (ctx?.tutorUid ? { id: ctx.tutorUid, name: ctx.tutorName || "Tutor" } : null);
      return fb ? [{ ...fb, seatsLeft: 3 }] : [];
    }
    const start = minutesToDate(dateObj, startMin);
    const end   = minutesToDate(dateObj, endMin);
    return (tutors || []).map((tutor) => {
      const blocks = dayAvailability.filter((a) => a.uid === tutor.id);
      if (!isFullyContainedInAvailability(blocks, start, end)) return null;
      if (!canFitTutor(daySessions, tutor.id, startMin, endMin)) return null;
      return { ...tutor, seatsLeft: seatsLeftForTutor(daySessions, tutor.id, startMin, endMin) };
    }).filter(Boolean);
  }, [ctx, tutors, dayAvailability, daySessions, dateObj, startMin, endMin, isDayClosed, isMasterMode]);

  const timeOptions = useMemo(() => {
    const latestStart = Math.max(dayRange.startMin, dayRange.endMin - 5);
    const opts = buildTimeOptions(dayRange.startMin, latestStart, 5);
    if (!opts.includes(startMin)) opts.push(startMin);
    return opts.sort((a, b) => a - b);
  }, [dayRange, startMin]);

  const endOptions = useMemo(() => {
    const minEnd = Math.min(dayRange.endMin, startMin + 5);
    const opts = buildTimeOptions(minEnd, dayRange.endMin, 5);
    if (!opts.includes(endMin)) opts.push(endMin);
    return opts.sort((a, b) => a - b).filter((v) => v > startMin);
  }, [dayRange, startMin, endMin]);

  useEffect(() => {
    if (!isOpen) return;
    if (startMin >= dayRange.endMin) {
      setStartMin(Math.max(dayRange.startMin, dayRange.endMin - 5));
      return;
    }
    if (endMin <= startMin || endMin > dayRange.endMin) {
      setEndMin(clamp(startMin + 110, startMin + 5, dayRange.endMin));
    }
  }, [dayRange, endMin, isOpen, startMin]);

  if (!isOpen || !ctx) return null;

  const handleCreate = async () => {
    if (isDayClosed) { alert("This day is marked as CLOSED. Please choose another date."); return; }
    if (!selectedStudent || !subject.trim()) { alert("Please select a student and enter a subject."); return; }
    if (!dateObj) { alert("Please choose a valid date."); return; }
    if (!tutorUid) { alert("Please select a tutor."); return; }
    if (endMin <= startMin) { alert("End time must be after start time."); return; }
    if (!isMasterMode && !availableTutors.some((t) => t.id === tutorUid)) {
      alert("The selected tutor is unavailable or fully booked for this time slot.");
      return;
    }
    const lane = resolveLane(daySessions, startMin, endMin, tutorUid);
    if (lane == null) { alert("This tutor already has 3 concurrent students for that time range."); return; }

    const start = minutesToDate(dateObj, startMin);
    const end   = minutesToDate(dateObj, endMin);
    const block = pickBlock(dayTimeBlocks, startMin, endMin, ctx.block);

    await onCreate({
      tutorUid,
      tutorName: selectedTutor?.name || ctx.tutorName || "Tutor",
      studentId: selectedStudent.id,
      studentName: selectedStudent.name,
      studentGrade: selectedStudent.grade,
      subject: subject.trim(),
      note: note.trim(),
      status: "Pending",
      start,
      end,
      dayKey: dayKeyFromDate(dateObj) || ctx.dayKey,
      blockId: block?.id || "",
      slotIdx: lane,
    });
    onClose();
  };

  // When the user clicks a day in the calendar, update the session date
  const handleCalendarDayClick = (date) => {
    setDateStr(dateKeyLocal(date));
  };

  return (
    <div className="evtModalOverlay" onMouseDown={onClose}>
      <div className="sessModal" onMouseDown={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sessModal__header">
          <div className="sessModal__title">Create Session</div>
          <button className="sessModal__closeBtn" onClick={onClose} aria-label="Close">
            <FiX size={14} />
          </button>
        </div>

        {/* Body: Calendar + Form */}
        <div className="sessModal__body">
          {/* ── Calendar Panel — real Student Calendar experience ── */}
          <div className="sessModal__calPanel">
            <div className="sessModal__calHeader">
              <div className="sessModal__calTitle">
                {selectedStudent ? `${selectedStudent.name}'s Calendar` : "Student Calendar"}
              </div>
              <div className="sessModal__calHint">
                {selectedStudent
                  ? "Click a day to set the session date. Session chips show existing sessions."
                  : "Select a student above to view their calendar."}
              </div>
            </div>

            <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
              <ModalCalendarPanel
                studentId={selectedStudentId || null}
                studentName={selectedStudent?.name || ""}
                selectedDate={dateObj}
                selectedSessionId={null}
                onDayClick={handleCalendarDayClick}
                onSessionClick={null}
                tutors={tutors}
              />
            </div>
          </div>

          {/* ── Form Panel ── */}
          <div className="sessModal__formPanel">
            <div className="sessModal__formHeader">
              <div className="sessModal__formTitle">Session Details</div>
              <div className="sessModal__formSub">
                {dateObj ? formatLongDate(dateObj) : "Choose a date in the calendar"}
              </div>
            </div>

            <div className="sessModal__fields">
              {/* Student */}
              <div className="sessModal__field">
                <label className="sessModal__label">Student</label>
                <div className="sessModal__dropdownWrapper" ref={dropdownRef}>
                  <input
                    className="sessModal__input"
                    type="text"
                    placeholder="Search student…"
                    value={selectedStudent ? selectedStudent.name : studentSearch}
                    onChange={(e) => { setStudentSearch(e.target.value); setSelectedStudentId(""); setShowDropdown(true); }}
                    onClick={() => setShowDropdown((p) => !p)}
                  />
                  {showDropdown && (
                    <div className="sessModal__dropdown">
                      {filteredStudents.length === 0 ? (
                        <div className="sessModal__dropdownItem" style={{ color: "#94a3b8" }}>No students found</div>
                      ) : filteredStudents.map((s) => (
                        <div
                          key={s.id}
                          className="sessModal__dropdownItem"
                          onClick={() => { setSelectedStudentId(s.id); setStudentSearch(s.name); setShowDropdown(false); }}
                        >
                          {s.name}
                          {s.grade ? <span style={{ color: "#94a3b8", fontSize: "0.85em", marginLeft: 4 }}>({s.grade})</span> : null}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Subject */}
              <div className="sessModal__field">
                <label className="sessModal__label">Subject</label>
                <SubjectSelect value={subject} onChange={setSubject} inputClassName="sessModal__input" />
              </div>

              {/* Date */}
              <div className="sessModal__field">
                <label className="sessModal__label">Date</label>
                <input
                  type="date"
                  className={`sessModal__input${isDayClosed ? " sessModal__input--error" : ""}`}
                  value={dateStr}
                  onChange={(e) => setDateStr(e.target.value)}
                />
                {isDayClosed && <div className="sessModal__closedBanner">This day is closed — please choose another date.</div>}
              </div>

              {/* Time row */}
              <div className="sessModal__field">
                <label className="sessModal__label">Time</label>
                <div className="sessModal__timeRow">
                  <select className="sessModal__select" value={startMin} onChange={(e) => setStartMin(Number(e.target.value))}>
                    {timeOptions.map((v) => <option key={v} value={v}>{formatTimeFromMinutes(v)}</option>)}
                  </select>
                  <select className="sessModal__select" value={endMin} onChange={(e) => setEndMin(Number(e.target.value))}>
                    {endOptions.map((v) => <option key={v} value={v}>{formatTimeFromMinutes(v)}</option>)}
                  </select>
                </div>
                <div className="sessModal__infoRow">
                  Available: {formatTimeFromMinutes(dayRange.startMin)} – {formatTimeFromMinutes(dayRange.endMin)}
                </div>
              </div>

              {/* Tutor */}
              <div className="sessModal__field">
                <label className="sessModal__label">Tutor</label>
                <select className="sessModal__select" value={tutorUid} onChange={(e) => setTutorUid(e.target.value)}>
                  {isDayClosed ? (
                    <option value="">— Closed —</option>
                  ) : (
                    <>
                      <option value="">Select tutor…</option>
                      {availableTutors.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name || t.email} ({t.seatsLeft} seat{t.seatsLeft !== 1 ? "s" : ""} left)
                        </option>
                      ))}
                      {tutorUid && !availableTutors.find((t) => t.id === tutorUid) && (
                        <option value={tutorUid}>
                          {selectedTutor?.name || ctx.tutorName || "Current"} (unavailable)
                        </option>
                      )}
                    </>
                  )}
                </select>
              </div>

              {/* Note */}
              <div className="sessModal__field">
                <label className="sessModal__label">Short Note</label>
                <textarea
                  className="sessModal__textarea"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Optional note…"
                />
              </div>
            </div>

            <div className="sessModal__actions">
              <button className="sessModal__btnCancel" onClick={onClose}>Cancel</button>
              <button className="sessModal__btnPrimary" onClick={handleCreate} disabled={isDayClosed}>
                Create Session
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
